// Copyright © 2024 Ory Corp
// SPDX-License-Identifier: Apache-2.0

import React from 'react'
import { LoginFlow, UpdateLoginFlowBody } from '@ory/client'
import { UserAuthCard } from '@ory/elements'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sdk, useSdkError } from '../services/sdk'
import AuthLayout from '../components/layout/AuthLayout'
import { SpinnerIcon } from '@sistemiv/s-components'
// import GoogleIcon from '../assets/icons/GogleIcon'
// import MicrosoftIcon from '../assets/icons/MicrosoftIcon'
// import { Button, Input } from '@sistemiv/s-components'
// import { filterNodesByGroups, isUiNodeInputAttributes } from '@ory/integrations/ui'

/**
 * Login is a React component that renders the login form using Ory Elements.
 * It is used to handle the login flow for a variety of authentication methods
 * and authentication levels (e.g. Single-Factor and Two-Factor)
 *
 * The Login component also handles the OAuth2 login flow (as an OAuth2 provider)
 * For more information regarding OAuth2 login, please see the following documentation:
 * https://www.ory.sh/docs/oauth2-oidc/custom-login-consent/flow
 *
 */
export const Login = (): JSX.Element => {
  const [flow, setFlow] = useState<LoginFlow | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const aal2 = searchParams.get('aal2')
  const loginChallenge = searchParams.get('login_challenge')
  const returnTo = searchParams.get('return_to')
  const [flowId, setFlowId] = useState<string | null>(null)
  const navigate = useNavigate()

  // Get the flow based on the flowId in the URL (.e.g redirect to this page after flow initialized)
  const getFlow = useCallback(
    (flowId: string) =>
      sdk
        // the flow data contains the form fields, error messages and csrf token
        .getLoginFlow({ id: flowId })
        .then(({ data: flow }) => setFlow(flow))
        .catch(sdkErrorHandler),
    //eslint-disable-next-line
    [],
  )

  // initialize the sdkError for generic handling of errors
  const sdkErrorHandler = useSdkError(getFlow, setFlow, '/login', true)

  // Create a new login flow
  const createFlow = React.useCallback(() => {
    sdk
      .createBrowserLoginFlow({
        refresh: true,
        aal: aal2 ? 'aal2' : 'aal1',
        //aal: "aal2",
        ...(loginChallenge && { loginChallenge: loginChallenge }),
        ...(returnTo ? { returnTo: returnTo } : {}),
      })
      // flow contains the form fields and csrf token
      .then(({ data: flow }) => {
        // Update URI query params to include flow id
        setSearchParams({ ['flow']: flow.id })
        // Set the flow data
        setFlow(flow)
      })
      .catch(sdkErrorHandler)
  }, [sdkErrorHandler, setSearchParams, setFlow, aal2, loginChallenge, returnTo])

  // submit the login form data to Ory
  const submitFlow = (body: UpdateLoginFlowBody) => {
    // something unexpected went wrong and the flow was not set
    if (!flow) return navigate('/login', { replace: true })

    // we submit the flow to Ory with the form data
    sdk
      .updateLoginFlow({ flow: flow.id, updateLoginFlowBody: body })
      .then(() => {
        // we successfully submitted the login flow, so lets redirect to the dashboard
        // navigate(flow.return_to ? flow.return_to : '/', { replace: true })
        if (flow.return_to?.startsWith('http')) {
          window.location.href = flow.return_to
        } else {
          navigate(flow.return_to || '/', { replace: true })
        }
      })
      .catch(sdkErrorHandler)
  }

  useEffect(() => {
    const newFlowId = searchParams.get('flow')
    if (!flow || flowId !== newFlowId) {
      if (flowId !== newFlowId) {
        setFlowId(newFlowId)
      }
      // we might redirect to this page after the flow is initialized, so we check for the flowId in the URL
      // the flow already exists
      if (newFlowId) {
        getFlow(newFlowId).catch(createFlow) // if for some reason the flow has expired, we need to get a new one
        return
      }

      // we assume there was no flow, so we create a new one
      createFlow()
    }
  }, [createFlow, getFlow, searchParams, flow, flowId])

  return (
    <AuthLayout>
      {flow ? (
        <UserAuthCard
          flowType={'login'}
          flow={flow}
          className='custom-auth-card !min-w-[calc(100vw-4rem)] sm:!min-w-[400px] p-4 sm:p-6 rounded-md sm:rounded-lg'
          additionalProps={{
            forgotPasswordURL: {
              handler: () => {
                const search = new URLSearchParams()
                if (flow.return_to) search.set('return_to', flow.return_to)
                navigate(
                  {
                    pathname: '/recovery',
                    search: search.toString(),
                  },
                  { replace: true },
                )
              },
            },
            signupURL: {
              handler: () => {
                const search = new URLSearchParams()
                if (flow.return_to) search.set('return_to', flow.return_to)
                if (flow.oauth2_login_challenge) search.set('login_challenge', flow.oauth2_login_challenge)

                navigate(
                  {
                    pathname: '/registration',
                    search: search.toString(),
                  },
                  { replace: true },
                )
              },
            },
          }}
          includeScripts
          onSubmit={({ body }) => submitFlow(body as UpdateLoginFlowBody)}
        />
      ) : (
        <SpinnerIcon className='w-6 h-6 text-sky-500 animate-spin' />
      )}
    </AuthLayout>
  )
}
